/**
 * Pure helpers for the floating drawing quick-edit bar.
 *
 * Kept out of the .tsx so they unit-test without a DOM, and so the component
 * file stays Fast-Refresh-safe (component-only exports).
 */

/** Line widths offered by the width control, in px. */
export const QUICK_BAR_WIDTHS = [1, 2, 3, 4] as const

/** Opacity steps offered by the opacity control, as percentages. */
export const QUICK_BAR_OPACITIES = [25, 50, 75, 100] as const

/**
 * Stored opacity (0..1) → display percent.
 *
 * `undefined` means the drawing never set one, which is FULLY OPAQUE — not
 * transparent. Returning 0 here would make every unstyled drawing read as
 * invisible in the control.
 */
export function opacityToPercent(opacity: number | undefined): number {
  if (opacity === undefined) return 100
  return Math.round(Math.min(1, Math.max(0, opacity)) * 100)
}

/** Display percent → stored opacity (0..1), clamped. */
export function percentToOpacity(percent: number): number {
  return Math.min(1, Math.max(0, percent / 100))
}

export interface QuickBarSize {
  width: number
  height: number
}
export interface QuickBarPoint {
  x: number
  y: number
}

/**
 * Keep the bar fully inside the chart area.
 *
 * The bar is user-draggable, so without this it can be parked off-screen and
 * become unreachable. `Math.max(0, …)` guards the case where the bar is wider
 * than the chart (narrow panes), which would otherwise yield a negative
 * coordinate and push it further out.
 */
export function clampQuickBarPosition(
  pos: QuickBarPoint,
  size: QuickBarSize,
  bounds: QuickBarSize,
): QuickBarPoint {
  return {
    x: Math.min(Math.max(0, bounds.width - size.width), Math.max(0, pos.x)),
    y: Math.min(Math.max(0, bounds.height - size.height), Math.max(0, pos.y)),
  }
}
